/**
 * Reusable Bar Chart Component
 * Built with Recharts for categorical and time-based bar visualization
 */

import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { format } from 'date-fns';

interface ChartSeries {
  name: string;
  dataKey: string;
  color: string;
  unit: string;
}

interface BarChartProps {
  data: any[];
  series: ChartSeries[];
  height?: number;
  showLegend?: boolean;
  xAxisKey?: string;
  xAxisFormatter?: (value: any) => string;
  yAxisDomain?: [number, number] | ['auto', 'auto'];
  loading?: boolean;
  stacked?: boolean;
  showZeroLine?: boolean;
}

export function BarChart({
  data,
  series,
  height = 350,
  showLegend = true,
  xAxisKey = 'timestamp',
  xAxisFormatter = (value) => format(new Date(value), 'HH:mm'),
  yAxisDomain = ['auto', 'auto'],
  loading = false,
  stacked = false,
  showZeroLine = false
}: BarChartProps) {

  if (loading) {
    return (
      <div className="animate-pulse bg-slate-800/30 rounded-lg" style={{ height }}>
        <div className="flex items-center justify-center h-full text-slate-500">
          Loading chart data...
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-slate-800/30 rounded-lg flex items-center justify-center" style={{ height }}>
        <p className="text-slate-400">No data available</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart
        data={data}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />

        <XAxis
          dataKey={xAxisKey}
          tickFormatter={xAxisFormatter}
          stroke="#94A3B8"
          style={{ fontSize: '12px' }}
        />

        <YAxis
          domain={yAxisDomain}
          stroke="#94A3B8"
          style={{ fontSize: '12px' }}
        />

        <Tooltip
          cursor={false}
          contentStyle={{
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '8px',
            color: '#E2E8F0'
          }}
          labelFormatter={(label) => {
            try {
              return format(new Date(label), 'MMM d, yyyy HH:mm');
            } catch {
              return label;
            }
          }}
          formatter={(value: number | string, name: string) => {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
              return [value, name];
            }
            return [value.toFixed(1), name];
          }}
        />

        {showLegend && (
          <Legend
            wrapperStyle={{ color: '#94A3B8' }}
            iconType="square"
          />
        )}

        {showZeroLine && (
          <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
        )}

        {series.map((s) => (
          <Bar
            key={s.dataKey}
            dataKey={s.dataKey}
            fill={s.color}
            name={`${s.name} (${s.unit})`}
            animationDuration={300}
            stackId={stacked ? '1' : undefined}
          />
        ))}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
